import React, { useEffect, useRef, useState } from "react";
import { AspectRatio, Box, type BoxProps, TabList, TabPanel, TabPanels, Tabs, type SpaceProps } from "@chakra-ui/react";
import { AnimatePresence, useInView } from "framer-motion";
import { type Theme } from "contexts/color-theme";
import { MotionBox } from "ui/chakra-motion";
import { TextColumn, type TextColumnProps } from "ui/text";
import { ProgressTab, type ProgressTabProps } from "./progress-tab";

const fadeIn = {
  initial: { opacity: 0.5 },
  animate: { opacity: 1, transition: { duration: 0.4 } },
  exit: { opacity: 0.5, transition: { duration: 0.4 } },
};

export const progressTabMediaRatio = { base: 1 / 1, sm: 1318 / 700, md: 1318 / 587 };

export type ProgressTabsProps = {
  /**
   * The color theme of the tab content.
   *
   * @default "light"
   */
  theme?: Theme;

  /**
   * The tabs to display. Each has a progress bar when active.
   */
  tabs: ProgressTabProps[];

  /**
   * The HTML element to render the progress tab title as.
   *
   * @default "h3" is inherited by `TextColumn`
   */
  tabTitleAs?: TextColumnProps["titleAs"];

  /**
   * The duration of the progress bar animation in seconds.
   *
   * @default 6
   */
  progressDuration?: number;

  /**
   * Callback when the tab changes.
   */
  onChangeTab?: (tabIndex: number) => void;

  /**
   * Optional min height to set on the mobile text column to help prevent
   * any layout shifting when the tabs have different lengths of copy.
   */
  textMinH?: BoxProps["minH"];

  /**
   * This setting is used to prerender all tab media items in the DOM.
   * This allows all static image files to load at once, fixing any "flashing" effect when the active tab changes.
   *
   * @default true
   */
  prerenderMedia?: boolean; // eslint-disable-line react/boolean-prop-naming
} & SpaceProps &
  Pick<BoxProps, "display">;

export function ProgressTabs({
  theme = "light",
  progressDuration = 6,
  prerenderMedia = true,
  tabs,
  tabTitleAs,
  textMinH,
  onChangeTab,
  ...spaceProps
}: ProgressTabsProps) {
  const [activeTab, setActiveTab] = useState(0);
  const scope = useRef<HTMLDivElement>(null);
  const isInView = useInView(scope, { once: true });
  const activeTabContent = tabs[activeTab];

  useEffect(() => {
    onChangeTab?.(activeTab);
  }, [activeTab, onChangeTab]);

  let prerenderedMedia = null;
  if (prerenderMedia) {
    prerenderedMedia = (
      <Box pos="absolute" boxSize="0px" opacity={0} aria-hidden="true">
        {tabs.map((tab) => (
          <Box key={tab.id}>{tab.media}</Box>
        ))}
      </Box>
    );
  }

  return (
    <Box ref={scope} w="100%" {...spaceProps}>
      <Tabs
        index={activeTab}
        variant="unstyled"
        display="flex"
        flexDir="column"
        gap={{ base: "smaller", md: "big" }}
        onChange={(tabIndex) => {
          setActiveTab(tabIndex);
        }}
      >
        <TabPanels>
          {tabs.map((tab, index) => (
            <TabPanel key={tab.id} pos="relative" boxSize="100%" p={0}>
              <AnimatePresence initial={false}>
                <MotionBox
                  key={`${tab.id}-media`}
                  variants={fadeIn}
                  initial="initial"
                  animate={activeTab === index ? "animate" : "exit"}
                  exit="exit"
                >
                  <AspectRatio
                    borderRadius="small"
                    ratio={progressTabMediaRatio}
                    w="100%"
                    overflow="hidden"
                    pos="relative"
                  >
                    {tab.media}
                  </AspectRatio>
                </MotionBox>
              </AnimatePresence>
            </TabPanel>
          ))}
        </TabPanels>

        <TabList gap="small">
          {tabs.map(({ onProgressEnd, ...tab }, index) => (
            <ProgressTab
              key={tab.id}
              // Do not begin progress animation until the component comes into view for the first time
              isActive={isInView && index === activeTab}
              theme={theme}
              titleAs={tabTitleAs}
              progressDuration={progressDuration}
              onProgressEnd={() => {
                setActiveTab(index === tabs.length - 1 ? 0 : activeTab + 1);
              }}
              {...tab}
            />
          ))}
        </TabList>
      </Tabs>

      <TextColumn
        display={{ base: "flex", md: "none" }}
        minH={textMinH}
        title={activeTabContent.title}
        titleAs={tabTitleAs}
        description={activeTabContent.description}
        theme={theme}
        mt="tiny"
      />

      {prerenderedMedia}
    </Box>
  );
}
